// The month-navigation row: filter · ‹ · 今天 · › (W40.1 F7).
//
// Lifted out of `routes/Calendar.tsx` for the reason AGENTS.md §3 #10 names when
// it says 超就当场拆: the route was at the 15 KB ceiling after the create form
// moved out, and the remedy is a split, not a re-baselining. This row is the
// cleanest candidate in the file — it owns no state of its own, every value it
// renders is passed in, and it has never needed a test because its four controls
// are asserted through the page (`Calendar.cta.test.tsx` drives all four).

import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";

/**
 * The five controls share one chrome, and it is the **shipped** chrome.
 *
 * W48.0: the row moved from `justify-between` (title left, controls right) to one
 * centred group — `‹` `今天` `2026 年 10 月` `›` `⚙` — because the month selector
 * being pinned to the left edge meant it was never in the same place twice and
 * read as a label rather than as the control it is.
 *
 * All three icon buttons are `tap-target press rounded-pill text-ink-2
 * hover:bg-black/5`, which is what `CalendarMonthNav` already used. The user's
 * 「保留之前那種 ios 的樣式」 named the filter button specifically, but leaving the
 * two arrows as white circles with a shadow while the filter went back to a bare
 * pill would have put three parts from three different eras in one 44px row.
 * `SlidersHorizontal` is restored rather than the labelled white pill the W48.0
 * mockup had invented.
 *
 * **`今天` is centred inside its own box, not merely in the row.** It is the only
 * text control here; given `height: 44px` alone its glyphs sit on the top edge of
 * that box, and `align-items: center` on the row does not fix that — the row
 * centres the *box*. `flex items-center justify-center` on the control is what
 * puts it on the same line as the icons.
 *
 * **`shrink-0` on all four controls is load-bearing, and it was found by
 * measurement rather than by reading.** At 320 the row is a few px wider than its
 * content box, and flex items shrink by default — so 「今天」 rendered **36px**
 * wide, below the 44pt floor, with nothing anywhere reporting it. `shrink-0`
 * makes that impossible: the row either fits or overflows *visibly*, and the
 * month title is the only thing allowed to give, via its own font size.
 */

export interface CalendarMonthNavProps {
  /** Rendered as 「YYYY 年 N 月」. */
  month: number;
  /** The year in that label. Never hidden — see the note above. */
  year: number;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onOpenFilter: () => void;
}

export function CalendarMonthNav({
  month,
  year,
  onPrev,
  onNext,
  onToday,
  onOpenFilter,
}: CalendarMonthNavProps) {
  return (
    // `md:justify-between` restores Q25's desktop arrangement — a left-aligned
    // title with the controls on the right — because on a centred desktop column
    // a centred five-item row reads as a toolbar, and the month is a heading
    // there, not a control.
    //
    // The `max-[359px]` pair is the narrow-width budget, measured rather than
    // guessed. With the year kept and all four controls pinned at 44 by
    // `shrink-0`, the row needs ~292px at `text-h3`; the content box at 320 is
    // 280. So below 360 the row's own padding goes to zero and the title drops
    // to `text-meta` — **shrinking type and gutter, never deleting a word.**
    //
    // The first version of this instead *hid the year* below 360px. The user
    // rejected it — 「monthly selector 要加上年份」 — and it was the worse trade
    // anyway: a month picker that reads 「10 月」 on one phone and
    // 「2026 年 10 月」 on another is a control that means two different things
    // depending on hardware.
    <div className="flex items-center justify-center gap-0.5 px-s-3 pb-s-3 max-[359px]:gap-0 max-[359px]:px-0 md:justify-between md:px-s-4">
      <button
        type="button"
        className="tap-target press flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-ink-2 hover:bg-black/5"
        onClick={onPrev}
        aria-label="上個月"
        data-testid="calendar-prev-month"
      >
        <ChevronLeft size={18} strokeWidth={1.5} aria-hidden="true" />
      </button>
      <button
        type="button"
        // Was 44x20. `tap-target-none` (defined nowhere) left the box at the
        // text's line-height; `h-11 w-11` is the real control's box. W48.0 adds
        // the centring a 44px box of CJK text needs — see the note above.
        //
        // `w-11`, not `px-2.5`: the padding version measured 48px wide (28px of
        // glyphs + 20px of padding), so this control was 4px wider than the three
        // beside it and pushed the month title 2px off the row's centre — a
        // five-item row that claims to be centred and is not.
        className="press flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-body-sm text-ink-2 hover:bg-black/5"
        onClick={onToday}
        data-testid="calendar-today"
      >
        今天
      </button>
      {/* The full 「YYYY 年 N 月」 is the label, at every width.
          W48.0 first hid the year below 360px and kept the whole string on
          `aria-label`; the user sent it back — 「monthly selector 要加上年份」.
          Two places spelling the same sentence is also how they drift, so there
          is one place now, and `max-[359px]:text-body-lg` below buys the
          narrow-width room by shrinking type instead of deleting a word. */}
      <h2
        data-testid="calendar-month-title"
        className="shrink-0 whitespace-nowrap px-1 text-h3 tabular-nums text-ink-1 max-[359px]:px-0.5 max-[359px]:text-body-sm"
      >
        {year} 年 {month} 月
      </h2>
      <button
        type="button"
        className="tap-target press flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-ink-2 hover:bg-black/5"
        onClick={onNext}
        aria-label="下個月"
        data-testid="calendar-next-month"
      >
        <ChevronRight size={18} strokeWidth={1.5} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="tap-target press flex h-11 w-11 shrink-0 items-center justify-center rounded-pill text-ink-2 hover:bg-black/5"
        onClick={onOpenFilter}
        aria-label="篩選"
        data-testid="calendar-open-filter"
      >
        <SlidersHorizontal size={18} strokeWidth={1.5} aria-hidden="true" />
      </button>
    </div>
  );
}
